import { useEffect, useState } from 'react';
import { Loader2, ShieldAlert, Settings, ToggleRight } from 'lucide-react';
import { Skeleton, ListSkeleton, PanelSkeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PageHeader } from '@/components/ui/page-header';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useSystemModuleStore } from '../store';
import { FlagType, type FeatureFlagMetadata, type FeatureFlagValue, type SystemModuleDto } from '../types';

const MODULE_DESCRIPTIONS: Record<string, string> = {
  MODM: 'Controls access to module management settings.',
  AUTM: 'Controls access to role and permission management.',
  USEM: 'Controls access to user account management.',
  ISSE: 'Controls access to material request issuance workflows.',
  RECV: 'Controls access to warehouse receiving and put-away workflows.',
};

function ModuleStatusBadge({ module }: { module: SystemModuleDto }) {
  if (!module.isEnabled) return <Badge variant="destructive">Disabled</Badge>;
  if (module.isUnderMaintenance) return <Badge variant="warning">Maintenance</Badge>;
  return <Badge variant="success">Active</Badge>;
}

// ─── Left Panel: Module List ───────────────────────────────────────────────

interface ModuleListProps {
  modules: SystemModuleDto[];
  selectedModule: SystemModuleDto | null;
  onSelect: (module: SystemModuleDto) => void;
}

function ModuleList({ modules, selectedModule, onSelect }: ModuleListProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <p className="text-sm font-semibold">Modules</p>
        <p className="text-xs text-muted-foreground mt-0.5">Select a module to configure</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="divide-y">
          {modules.map((mod) => (
            <button
              key={mod.id}
              onClick={() => onSelect(mod)}
              className={cn(
                'w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors',
                selectedModule?.id === mod.id && 'bg-muted',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{mod.moduleName}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <code>{mod.moduleCode}</code>
                  </div>
                </div>
                <ModuleStatusBadge module={mod} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Feature Flag Control (Switch for boolean, NumberInput for number) ────

interface FlagControlProps {
  flag: FeatureFlagMetadata;
  currentValue: FeatureFlagValue | undefined;
  isSaving: boolean;
  onChange: (value: FeatureFlagValue) => void;
}

function FlagControl({ flag, currentValue, isSaving, onChange }: FlagControlProps) {
  const effective = currentValue ?? flag.defaultValue;

  if (flag.type === FlagType.Boolean) {
    return (
      <div className="flex items-start justify-between gap-6">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">{flag.label}</Label>
          <p className="text-xs text-muted-foreground">{flag.description}</p>
        </div>
        <Switch
          checked={Boolean(effective)}
          disabled={isSaving}
          onCheckedChange={(v) => onChange(v)}
        />
      </div>
    );
  }

  // Number: local draft state so the user can type freely without spamming saves;
  // commit on blur or Enter.
  const numeric = typeof effective === 'number' ? effective : Number(flag.defaultValue);
  return (
    <NumberFlagInput
      flag={flag}
      value={numeric}
      isSaving={isSaving}
      onCommit={onChange}
    />
  );
}

interface NumberFlagInputProps {
  flag: FeatureFlagMetadata;
  value: number;
  isSaving: boolean;
  onCommit: (value: number) => void;
}

function NumberFlagInput({ flag, value, isSaving, onCommit }: NumberFlagInputProps) {
  const [draft, setDraft] = useState<string>(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      setDraft(String(value));
      return;
    }
    const clamped = Math.min(
      Math.max(parsed, flag.min ?? Number.NEGATIVE_INFINITY),
      flag.max ?? Number.POSITIVE_INFINITY,
    );
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <div className="flex items-start justify-between gap-6">
      <div className="space-y-0.5">
        <Label className="text-sm font-medium">{flag.label}</Label>
        <p className="text-xs text-muted-foreground">{flag.description}</p>
      </div>
      <Input
        type="number"
        value={draft}
        min={flag.min ?? undefined}
        max={flag.max ?? undefined}
        disabled={isSaving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        className="w-24 text-right"
      />
    </div>
  );
}

// ─── Right Panel: Module Detail ────────────────────────────────────────────

interface ModuleDetailProps {
  module: SystemModuleDto;
}

function ModuleDetail({ module }: ModuleDetailProps) {
  const updateModule = useSystemModuleStore((s) => s.updateModule);
  const isSaving = useSystemModuleStore((s) => s.isSaving);
  const featureFlagMetadata = useSystemModuleStore((s) => s.featureFlagMetadata);

  const [message, setMessage] = useState(module.maintenanceMessage ?? '');
  const [isDirty, setIsDirty] = useState(false);

  // Sync local message state when the selected module changes
  useEffect(() => {
    setMessage(module.maintenanceMessage ?? '');
    setIsDirty(false);
  }, [module.id, module.maintenanceMessage]);

  const flagMetadata: FeatureFlagMetadata[] = featureFlagMetadata[module.moduleCode] ?? [];

  const isModm = module.moduleCode === 'MODM';

  const handleToggle = async (
    field: 'isEnabled' | 'isUnderMaintenance' | 'allowAdminOverride',
    value: boolean,
  ) => {
    try {
      await updateModule(module.id, {
        isEnabled: field === 'isEnabled' ? value : module.isEnabled,
        isUnderMaintenance: field === 'isUnderMaintenance' ? value : module.isUnderMaintenance,
        allowAdminOverride: field === 'allowAdminOverride' ? value : module.allowAdminOverride,
        maintenanceMessage: module.maintenanceMessage,
      });
      toast.success(`${module.moduleName} updated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update module');
    }
  };

  const handleFlagChange = async (flagKey: string, value: FeatureFlagValue) => {
    try {
      const updatedFlags = { ...module.featureFlags, [flagKey]: value };
      await updateModule(module.id, {
        isEnabled: module.isEnabled,
        isUnderMaintenance: module.isUnderMaintenance,
        allowAdminOverride: module.allowAdminOverride,
        maintenanceMessage: module.maintenanceMessage,
        featureFlags: updatedFlags,
      });
      toast.success(`Feature flag updated`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update feature flag');
    }
  };

  const handleSaveMessage = async () => {
    try {
      await updateModule(module.id, {
        isEnabled: module.isEnabled,
        isUnderMaintenance: module.isUnderMaintenance,
        allowAdminOverride: module.allowAdminOverride,
        maintenanceMessage: message.trim() || null,
      });
      setIsDirty(false);
      toast.success('Maintenance message saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save message');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {module.moduleCode}
              </span>
              {module.moduleName}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {MODULE_DESCRIPTIONS[module.moduleCode] ?? module.description}
            </p>
          </div>
          <ModuleStatusBadge module={module} />
        </div>
      </div>

      {/* Scrollable settings */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {isModm && (
          <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            This module cannot be disabled or put into maintenance mode.
          </div>
        )}

        {/* Toggle: Enabled */}
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Enabled</Label>
            <p className="text-xs text-muted-foreground">
              When disabled, all users (including admins) are blocked from this module.
            </p>
          </div>
          <Switch
            checked={module.isEnabled}
            disabled={isModm || isSaving}
            onCheckedChange={(v) => handleToggle('isEnabled', v)}
          />
        </div>

        <div className="border-t" />

        {/* Toggle: Under Maintenance */}
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Under Maintenance</Label>
            <p className="text-xs text-muted-foreground">
              Regular users are blocked; admins may bypass if override is enabled.
            </p>
          </div>
          <Switch
            checked={module.isUnderMaintenance}
            disabled={isModm || isSaving}
            onCheckedChange={(v) => handleToggle('isUnderMaintenance', v)}
          />
        </div>

        <div className="border-t" />

        {/* Toggle: Allow Admin Override */}
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-0.5">
            <Label className="text-sm font-medium">Allow Admin Override</Label>
            <p className="text-xs text-muted-foreground">
              Admins bypass maintenance mode when this is enabled.
            </p>
          </div>
          <Switch
            checked={module.allowAdminOverride}
            disabled={isSaving}
            onCheckedChange={(v) => handleToggle('allowAdminOverride', v)}
          />
        </div>

        <div className="border-t" />

        {/* Maintenance Message */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Maintenance Message</Label>
          <p className="text-xs text-muted-foreground">
            Shown to blocked users. Leave blank for the default message.
          </p>
          <Textarea
            placeholder="e.g. This module is temporarily unavailable for scheduled maintenance."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setIsDirty(e.target.value !== (module.maintenanceMessage ?? ''));
            }}
            rows={3}
            maxLength={500}
            className="resize-none text-sm"
          />
          {isDirty && (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveMessage} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                Save Message
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={isSaving}
                onClick={() => {
                  setMessage(module.maintenanceMessage ?? '');
                  setIsDirty(false);
                }}
              >
                Discard
              </Button>
            </div>
          )}
        </div>

        {/* Feature Settings */}
        {flagMetadata.length > 0 && (
          <>
            <div className="border-t" />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <ToggleRight className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-semibold">Feature Settings</Label>
              </div>
              {flagMetadata.map((flag) => (
                <FlagControl
                  key={flag.key}
                  flag={flag}
                  currentValue={module.featureFlags?.[flag.key]}
                  isSaving={isSaving}
                  onChange={(value) => handleFlagChange(flag.key, value)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function SystemModulesPage() {
  usePageTitle('Module Management');
  const { modules, isLoading, error, fetchModules, fetchFeatureFlagMetadata } = useSystemModuleStore();
  const [selectedModule, setSelectedModule] = useState<SystemModuleDto | null>(null);

  // Both are admin-gated and only needed here — the metadata fetch used to ride along with the app-shell
  // module load, which is half of why every non-admin got a 403 toast on login (86d3va61r). Fired in
  // parallel: the flag editors need the metadata, but the module list shouldn't wait on it.
  useEffect(() => {
    fetchModules();
    fetchFeatureFlagMetadata();
  }, [fetchModules, fetchFeatureFlagMetadata]);

  // Auto-select first module once loaded
  useEffect(() => {
    if (modules.length > 0 && !selectedModule) {
      setSelectedModule(modules[0]);
    }
  }, [modules, selectedModule]);

  // Keep selected module in sync with store updates (e.g. after a toggle).
  // selectedModule is intentionally excluded — adding it would cause an infinite loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedModule) {
      const updated = modules.find((m) => m.id === selectedModule.id);
      if (updated) setSelectedModule(updated);
    }
  }, [modules]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Settings className="h-5 w-5 text-muted-foreground" />
            Module Management
          </h1>
        }
        description="Control the availability of each system module. Disabling a module blocks all users; maintenance mode blocks regular users while admins can bypass."
      />

      {/* List + Detail pane */}
      {isLoading ? (
        <div className="flex h-[calc(100vh-16rem)] border rounded-lg overflow-hidden">
          {/* Left panel */}
          <div className="w-72 border-r bg-muted/20 flex-shrink-0">
            <div className="p-4 border-b space-y-1.5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-40" />
            </div>
            <div className="p-4">
              <ListSkeleton rows={5} />
            </div>
          </div>
          {/* Right panel */}
          <div className="flex-1 bg-background p-6">
            <PanelSkeleton rows={6} />
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{error}</p>
          {/* Retry both: whatever took out the module list almost certainly took out the metadata
              too, and recovering only the list would leave the flag editors silently missing. */}
          <Button
            variant="outline"
            onClick={() => {
              fetchModules();
              fetchFeatureFlagMetadata();
            }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <div className="flex h-[calc(100vh-16rem)] border rounded-lg overflow-hidden">
          {/* Left panel */}
          <div className="w-72 border-r bg-muted/20 flex-shrink-0">
            <ModuleList
              modules={modules}
              selectedModule={selectedModule}
              onSelect={setSelectedModule}
            />
          </div>

          {/* Right panel */}
          <div className="flex-1 bg-background">
            {selectedModule ? (
              <ModuleDetail module={selectedModule} />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <p className="text-lg font-medium">Select a module</p>
                  <p className="text-sm mt-1">Choose a module from the list to configure it</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
